'use client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthErrorCode } from 'types/auth';
import { ROLES, RoleType } from 'types/roles';

import { AuthForm, FormData } from '@components/AuthForm';
import { LoginWrapper } from '@components/Login';
import { SuccessMessage } from '@components/SuccessMessage';
import { CheckboxWithLink } from '@components/TermsCheckbox';
import { TextGroup } from '@components/TextGroups';
import { showToast } from '@components/ToastContainer';

import { signUp } from '@utils/supabase/auth-actions';

/**
 * Register component that handles user sign up.
 */
export function Register({ role }: { role: RoleType }) {
  const { t } = useTranslation('common');
  const [hasError, setHasError] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const handleFormSubmit = async (data: FormData) => {
    if (!agreeTerms) {
      setHasError(true);
      showToast('error', t('toast.errorAgreeTerms'), t('toast.errorAgreeTermsDescription'));
      return;
    }
    const response = await signUp(data, role);
    if (!response?.error) {
      setSuccess('success');
    } else if (response?.error) {
      setHasError(true);
      const code = response.error.code;
      if (code === AuthErrorCode.PASSWORD_TOO_SHORT) {
        showToast('error', t('toast.errorPassword'), t('toast.errorPasswordLength'));
      } else if (code === AuthErrorCode.EMAIL_INVALID) {
        showToast('error', t('toast.errorEmail'), t('toast.errorEmailDescription'));
      } else if (code === AuthErrorCode.EMAIL_CONFIRMATION_REQUIRED) {
        showToast('error', t('toast.errorEmail'), t('toast.errorEmailConfirmationRequiredDescription'));
      } else if (code === AuthErrorCode.EMAIL_MISMATCH) {
        showToast('error', t('toast.errorEmail'), t('toast.errorEmailMismatchDescription'));
      } else {
        showToast('error', t('toast.error'), t('toast.errorLoginDescription'));
      }
    }
  };

  if (success) {
    return (
      <SuccessMessage
        header={
          role === ROLES.SCHOOL ? t('register.verifyAccountSchool') : t('register.verifyAccount')
        }
        paragraph={
          role === ROLES.SCHOOL
            ? t('register.verifyAccountDescriptionSchool')
            : t('register.verifyAccountDescription')
        }
        illustrationSrc={'/assets/images/Illustration-verify.svg'}
        link={`/${role}/login`}
        buttonLabel={t('login.title')}
      />
    );
  }

  return (
    <LoginWrapper>
      <TextGroup
        header={role === ROLES.TEACHER ? t('register.titleTeacher') : t('register.titleSchool')}
        paragraph={t('register.description') + ' '}
        href={`/${role}/login`}
        linkLabel={t('register.loginThen')}
      />
      <AuthForm
        onSubmit={handleFormSubmit}
        hasError={hasError}
        labelButton={t('register.register')}
        requiresEmailConfirmation>
        <CheckboxWithLink
          label={t('register.termsAgree') + ' '}
          linkText={t('register.termsLink')}
          linkUrl={`/${role}/terms`}
          checked={agreeTerms}
          onChange={setAgreeTerms}
        />
      </AuthForm>
    </LoginWrapper>
  );
}
